import React, { useState } from 'react';

import type { UIAnalyticsEvent } from '@atlaskit/analytics-next';
import { ActionKeys } from '@atlassian/conversation-assistant-message-actions-api/constants';
import type { ActionRendererFor } from '@atlassian/conversation-assistant-message-actions-api/types';
import type { RovoLabsConfirmedTool } from '@atlassian/conversation-assistant-service-api/types/assistance-service';
import type { MessageActionInvocationStatus } from '@atlassian/conversation-assistant-service-api/types/message-actions';
import { fireErrorAnalytics } from '@atlassian/error-handling';

import { PACKAGE_NAME, TEAM_NAME } from './constants.tsx';
import type { AdditionalAnalyticAttributes } from './types';
import type { AgenticPlanningAction } from './types';

export const buildTool = ({
	action,
	resolutionType,
	dataArguments,
}: {
	action: AgenticPlanningAction;
	dataArguments?: unknown;
	resolutionType: 'confirmed' | 'cancelled';
}): RovoLabsConfirmedTool => ({
	key: action.key,
	tool_source: action.toolSource ?? undefined,
	invocation_id: action.invocationId,
	resolution: {
		type: resolutionType,
	},
	data: {
		human_message_id: action.humanMessageId ?? '',
		arguments: dataArguments ?? {},
	},
});

export const isAgenticPlanningAction = (actions: any[]): actions is AgenticPlanningAction[] =>
	actions.length > 0 && actions.every((a) => a?.data != null);

export type ChildrenProps = {
	handleOnDismiss: (e: React.MouseEvent, analyticsEvent: UIAnalyticsEvent) => void;
	localActionState: MessageActionInvocationStatus | undefined;
	/**
	 * Optimistically marks the action as finished (hides the footer, shows the
	 * accepted system message). Used by the OOTB publish flow, which completes
	 * the session without resuming via onConfirm.
	 */
	markFinished: () => void;
	message: { actions: AgenticPlanningAction[] };
	onConfirm: (
		args: { actions: AgenticPlanningAction[] },
		analyticsAttributes?: AdditionalAnalyticAttributes,
	) => void;
};

type Props = ActionRendererFor<
	typeof ActionKeys.AgenticPlanTool | typeof ActionKeys.MultipleChoiceQuestionsTool
> & {
	children: (props: ChildrenProps) => React.JSX.Element | null;
	interactionSource: 'agenticPlanToolRenderer' | 'multipleChoiceQuestionsToolRenderer';
};

const VALID_STATE = {
	[ActionKeys.AgenticPlanTool]: ['planned', 'finished', 'cancelled'],
	[ActionKeys.MultipleChoiceQuestionsTool]: ['planned', 'finished'],
};

export const AgenticPlanWrapperInChat = ({
	actions,
	recipientAgentId,
	conversationId,
	messageId,
	sendMessageConfirmedTools,
	interactionSource,
	children,
}: Props): React.JSX.Element | null => {
	const firstAction = actions[0];

	const [localActionState, setLocalActionState] = useState<
		MessageActionInvocationStatus | undefined
	>(firstAction?.status?.toLowerCase() as MessageActionInvocationStatus | undefined);

	if (!isAgenticPlanningAction(actions) || !actions[0]) {
		fireErrorAnalytics({
			meta: {
				id: interactionSource,
				packageName: PACKAGE_NAME,
				teamName: TEAM_NAME,
			},
			attributes: { messageId },
		});
		return null;
	}

	const agenticActions: AgenticPlanningAction[] = actions;

	if (
		!localActionState ||
		!(firstAction.key in VALID_STATE) ||
		!VALID_STATE[firstAction.key as keyof typeof VALID_STATE].includes(localActionState)
	) {
		return null;
	}

	const sendTools = (
		resolutionType: 'confirmed' | 'cancelled',
		tools: RovoLabsConfirmedTool[],
		analyticsAttributes?: AdditionalAnalyticAttributes,
	) => {
		sendMessageConfirmedTools({
			agentId: recipientAgentId,
			conversationId,
			messageSources: {
				interactionSource,
			},
			confirmed_tools: {
				message_id: messageId,
				tools,
			},
			actionedAnalyticsAttributes: analyticsAttributes,
		});
		setLocalActionState(resolutionType === 'confirmed' ? 'finished' : 'cancelled');
	};

	const onConfirm = (
		{ actions: confirmedActions }: { actions: AgenticPlanningAction[] },
		analyticsAttributes?: AdditionalAnalyticAttributes,
	) => {
		sendTools(
			'confirmed',
			confirmedActions.map((action) =>
				buildTool({ action, resolutionType: 'confirmed', dataArguments: action.data }),
			),
			{
				actionKey: firstAction.key,
				...analyticsAttributes,
			},
		);
	};

	const handleOnDismiss = (_e: React.MouseEvent, _analyticsEvent: UIAnalyticsEvent) => {
		sendTools(
			'cancelled',
			agenticActions.map((action) =>
				buildTool({ action, resolutionType: 'cancelled', dataArguments: action.data }),
			),
			{ agenticPlanDismissed: true, actionKey: firstAction.key },
		);
	};

	const message = { actions: agenticActions };

	return children({
		onConfirm,
		handleOnDismiss,
		message,
		localActionState,
		markFinished: () => setLocalActionState('finished'),
	});
};
